import { LoopSimulationStatus as ILoopSimulationStatus } from "../interfaces/loop-simulation-status";
import { calculateComplexity } from "./simulation-details-utility";

export class LoopSimulationStatus implements ILoopSimulationStatus {
  iterations: number;
  currentIteration: number;
  currentIterationSteps: number;
  currentIterationUniqueSteps: number;

  timeSinceLastIterationLog: number;
  averageTimePerIterationLog: number;
  runningTime: number;
  startingTime: number;
  estimatedTimeRemainingLog: number;
  lastUpdateTime: number;
  responseTimes: number[]; // Array to store response times
  stepsPerIteration: number[]; // Array to store steps per iteration
  averageStepsPerIteration: number;
  maxStepsPerIteration: number;
  minStepsPerIteration: number;

  maxUniqueStepsPerIteration: number;
  minUniqueStepsPerIteration: number;

  uniqueStepsPerIteration: number[];
  averageUniqueStepsPerIteration: number;
  startingAgents: number;
  landslideCount: number;

  complexityRating: number = 0;
  complexityRatings: number[] = [];
  averageComplexityRatings: number = 0;

  constructor(
    iterations: number,
    currentIteration: number,
    currentIterationSteps: number
  ) {
    this.iterations = Number(iterations);
    this.currentIteration = currentIteration;
    this.currentIterationSteps = currentIterationSteps;
    this.currentIterationUniqueSteps = 0;

    this.timeSinceLastIterationLog = 0;
    this.averageTimePerIterationLog = 0;
    this.estimatedTimeRemainingLog = 0;
    this.lastUpdateTime = Date.now();
    this.responseTimes = []; // Initialize responseTimes array
    this.stepsPerIteration = []; // Initialize stepsPerIteration array
    this.averageStepsPerIteration = 0;
    this.maxStepsPerIteration = 0;
    this.minStepsPerIteration = 0;

    this.maxUniqueStepsPerIteration = 0;
    this.minUniqueStepsPerIteration = 0;

    this.runningTime = 0;
    this.startingTime = Date.now();
    this.uniqueStepsPerIteration = [];
    this.averageUniqueStepsPerIteration = 0;
    this.startingAgents = 0;
    this.landslideCount = 0;
  }

  updateStats(totalUnique: number, totalSpawned: number, startingAgents: number, landslideCount: number): void {
    this.currentIteration++;
    this.currentIterationSteps = totalSpawned;
    this.currentIterationUniqueSteps = totalUnique;
    this.startingAgents = startingAgents;
    this.landslideCount = landslideCount;

    const currentTime: number = Date.now();
    const timeSinceLastUpdate: number = currentTime - this.lastUpdateTime;
    this.lastUpdateTime = currentTime;
    this.runningTime = currentTime - this.startingTime;

    if (this.currentIteration > this.iterations) {
      return;
    }

    this.responseTimes.push(timeSinceLastUpdate); // Store response time in the array
    this.stepsPerIteration.push(totalSpawned); // Store steps in the array
    this.uniqueStepsPerIteration.push(totalUnique); // Store steps in the array

    const sumResponseTimes: number = this.responseTimes.reduce((a, b) => a + b, 0);
    const averageTimePerIteration: number = Math.floor(sumResponseTimes / this.responseTimes.length);
    const remainingIterations: number = this.iterations - this.currentIteration;
    const estimatedTimeRemaining: number = remainingIterations > 0 ? averageTimePerIteration * remainingIterations: 0;

    this.averageStepsPerIteration = Math.floor(this.stepsPerIteration.reduce((a, b) => a + b, 0) / this.stepsPerIteration.length);
    this.averageUniqueStepsPerIteration = Math.floor(this.uniqueStepsPerIteration.reduce((a, b) => a + b, 0) / this.uniqueStepsPerIteration.length);

    this.maxStepsPerIteration = Math.max(...this.stepsPerIteration);
    this.minStepsPerIteration = Math.min(...this.stepsPerIteration);

    this.maxUniqueStepsPerIteration = Math.max(...this.uniqueStepsPerIteration);
    this.minUniqueStepsPerIteration = Math.min(...this.uniqueStepsPerIteration);

    this.timeSinceLastIterationLog = timeSinceLastUpdate;
    this.averageTimePerIterationLog = averageTimePerIteration;
    this.estimatedTimeRemainingLog = estimatedTimeRemaining;

    const complexityRating: number =  calculateComplexity(startingAgents, totalSpawned, totalUnique);
    this.complexityRating = complexityRating;
    this.complexityRatings.push(complexityRating)
    this.averageComplexityRatings = Math.floor(this.complexityRatings.reduce((a, b) => a + b, 0) / this.complexityRatings.length);
  }

  calculateUniquenessRatio(totalSpawned: number, uniqueSpawned: number): number {
    if (totalSpawned === 0) {
        return 0;
    }
    return totalSpawned / uniqueSpawned;
  }

  calcualteMaxAgentPercentage(initiationPoints: number, totalSpawned: number): number {
    if (totalSpawned === 0) {
        return 0;
    }

    return totalSpawned / initiationPoints;
  }


  calculateComplexity(
    startingNumber: number,
    totalSpawned: number,
    uniqueSpawned: number
): any {
    const duplicationFactor:number = this.calculateDuplicationFactor(totalSpawned, uniqueSpawned);
    const growthFactor:number = this.calculateGrowthFactor(totalSpawned, startingNumber);
    const uniqueGrowthFactor:number = this.calculateUniqueGrowthFactor(uniqueSpawned, startingNumber);

    // Store the results in an object

    return duplicationFactor * growthFactor / uniqueGrowthFactor;
}


calculateDuplicationFactor(totalSpawned: number, uniqueSpawned: number): number {
  if (uniqueSpawned === 0) {
      throw new Error("Unique spawned numbers cannot be zero.");
  }
  return totalSpawned / uniqueSpawned;
}

calculateGrowthFactor(totalSpawned: number, startingNumber: number): number {
  if (startingNumber === 0) {
      throw new Error("Starting number cannot be zero.");
  }
  return totalSpawned / startingNumber;
}

calculateUniqueGrowthFactor(uniqueSpawned: number, startingNumber: number): number {
  if (startingNumber === 0) {
      throw new Error("Starting number cannot be zero.");
  }
  return uniqueSpawned / startingNumber;
}


}
